import {
  Box,
  Center,
  CSSDimension,
  getCssSize,
  HStack,
  Integer,
  useFoundationTheme,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {LightboxAction} from '../types/LightboxAction';
import {IconButton} from './IconButton';

export type ActionPosition = {
  top: CSSDimension | Integer;
  right: CSSDimension | Integer;
  left: CSSDimension | Integer;
};

type CommonProps = RequiredTestIdProps & {
  actions: LightboxAction[][];
  zIndex: 'LIGHTBOX_ACTIONS' | 'LIGHTBOX_DETAIL_ACTIONS';
  isActive?: boolean;
  isHidden?: boolean;
};

type LightboxActionsProps =
  | (CommonProps & {
      actionPosition: ActionPosition;
      disablePosition?: boolean;
    })
  | (CommonProps & {
      actionPosition?: ActionPosition;
      disablePosition: true;
    });

const actionsVariants = cva('transition-opacity duration-150 ease-out', {
  variants: {
    disablePosition: {
      true: '',
      false: 'absolute',
    },
  },
});

export function Actions(props: LightboxActionsProps) {
  const theme = useFoundationTheme();
  const [isFocused, setFocused, setUnfocused] = useBoolean(false);

  return (
    <div
      className={cn(actionsVariants({disablePosition: Boolean(props.disablePosition)}))}
      style={{
        top: getCssSize(props.actionPosition?.top),
        left: getCssSize(props.actionPosition?.left),
        right: getCssSize(props.actionPosition?.right),
        zIndex: theme.zIndices[props.zIndex],
        opacity: props.isHidden && !isFocused ? 0 : 1,
      }}
      onFocus={setFocused}
      onBlur={setUnfocused}
    >
      <HStack spacing={2}>
        {props.actions.map((iconsGroup, index) => (
          <Box
            borderRadius="small"
            backgroundColor="palettes.black.900.70"
            key={`${index}-${iconsGroup.length}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
          >
            <Center height={9}>
              {iconsGroup.map((action) => (
                <IconButton
                  data-testid={suffixTestId(`actions-[${action.id}]`, props)}
                  key={action.id}
                  icon={action.icon}
                  isDisabled={action.isDisabled}
                  isLoading={action.isLoading}
                  tabIndex={props.isActive ? 0 : -1}
                  onClick={() => {
                    action.onClick(action.id);
                  }}
                />
              ))}
            </Center>
          </Box>
        ))}
      </HStack>
    </div>
  );
}
